import { EventEmitter } from '@alphahub/eie-core';

export interface IBaselineCalibrator {
  start: () => void;
  stop: () => void;
  feedSample: () => void;
  getBaseline: () => any | undefined;
}

type Getters = {
  getLatestBlendshapes: () => Record<string, number> | undefined;
};

type Setters = {
  setBaseline: (b: any) => void;
  pushTimelineState: (s: any) => void;
  emit: (event: string, payload: any) => void;
};

/** Standard MediaPipe blendshape keys used for personalised baseline. */
const FACS_KEYS: string[] = [
  'mouthSmileLeft', 'mouthSmileRight',
  'mouthFrownLeft', 'mouthFrownRight',
  'browDownLeft', 'browDownRight',
  'browInnerUp',
  'eyeWideLeft', 'eyeWideRight',
  'eyeSquintLeft', 'eyeSquintRight',
  'cheekSquintLeft', 'cheekSquintRight',
  'jawOpen',
  'mouthPucker', 'mouthLeft', 'mouthRight',
  'noseSneerLeft', 'noseSneerRight',
  'mouthShrugLower', 'mouthShrugUpper',
  'mouthPressLeft', 'mouthPressRight',
];

/** Conservative floor thresholds per blendshape. */
const DEFAULT_THRESHOLDS: Record<string, number> = {
  mouthSmileLeft: 0.25, mouthSmileRight: 0.25,
  mouthFrownLeft: 0.20, mouthFrownRight: 0.20,
  browDownLeft: 0.20, browDownRight: 0.20,
  browInnerUp: 0.20,
  eyeWideLeft: 0.15, eyeWideRight: 0.15,
  eyeSquintLeft: 0.20, eyeSquintRight: 0.20,
  cheekSquintLeft: 0.15, cheekSquintRight: 0.15,
  jawOpen: 0.25,
  mouthPucker: 0.20, mouthLeft: 0.20, mouthRight: 0.20,
  noseSneerLeft: 0.15, noseSneerRight: 0.15,
  mouthShrugLower: 0.25, mouthShrugUpper: 0.20,
  mouthPressLeft: 0.20, mouthPressRight: 0.20,
};

/** Symmetric blendshape pairs for asymmetry computation. */
const SYMMETRIC_PAIRS: [string, string][] = [
  ['mouthSmileLeft', 'mouthSmileRight'],
  ['mouthFrownLeft', 'mouthFrownRight'],
  ['browDownLeft', 'browDownRight'],
  ['eyeWideLeft', 'eyeWideRight'],
  ['eyeSquintLeft', 'eyeSquintRight'],
  ['cheekSquintLeft', 'cheekSquintRight'],
  ['noseSneerLeft', 'noseSneerRight'],
  ['mouthLeft', 'mouthRight'],
];

// ── Valence / Arousal helpers ──────────────────────────────────────────

function positiveSignal(bs: Record<string, number>): number {
  const smile = ((bs.mouthSmileLeft ?? 0) + (bs.mouthSmileRight ?? 0)) / 2;
  const cheek = ((bs.cheekSquintLeft ?? 0) + (bs.cheekSquintRight ?? 0)) / 2;
  return smile * 0.55 + cheek * 0.30 + (bs.browInnerUp ?? 0) * 0.15;
}

function negativeSignal(bs: Record<string, number>): number {
  const frown = ((bs.mouthFrownLeft ?? 0) + (bs.mouthFrownRight ?? 0)) / 2;
  const browDown = ((bs.browDownLeft ?? 0) + (bs.browDownRight ?? 0)) / 2;
  const sneer = ((bs.noseSneerLeft ?? 0) + (bs.noseSneerRight ?? 0)) / 2;
  const pucker = bs.mouthPucker ?? 0;
  return frown * 0.35 + browDown * 0.30 + sneer * 0.20 + pucker * 0.15;
}

function computeValence(bs: Record<string, number>): number {
  return Math.max(-1, Math.min(1, positiveSignal(bs) - negativeSignal(bs)));
}

function computeArousal(bs: Record<string, number>): number {
  const eyeWide = ((bs.eyeWideLeft ?? 0) + (bs.eyeWideRight ?? 0)) / 2;
  const jawOpen = bs.jawOpen ?? 0;
  const browDown = ((bs.browDownLeft ?? 0) + (bs.browDownRight ?? 0)) / 2;
  const browUp = bs.browInnerUp ?? 0;
  const sneer = ((bs.noseSneerLeft ?? 0) + (bs.noseSneerRight ?? 0)) / 2;
  const pucker = bs.mouthPucker ?? 0;
  const eyeSquint = ((bs.eyeSquintLeft ?? 0) + (bs.eyeSquintRight ?? 0)) / 2;
  return Math.max(0, Math.min(1,
    eyeWide * 0.25 + jawOpen * 0.20 + browDown * 0.15 +
    browUp * 0.10 + sneer * 0.10 + pucker * 0.10 + eyeSquint * 0.10,
  ));
}

// ── Derived clinical metrics ───────────────────────────────────────────

function computeFacialTension(avgBs: Record<string, number>): number {
  let sum = 0; let count = 0;
  for (const key of FACS_KEYS) { if (avgBs[key] != null) { sum += avgBs[key]; count++; } }
  return count > 0 ? +(sum / count).toFixed(4) : 0;
}

function computeAsymmetry(avgBs: Record<string, number>): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [l, r] of SYMMETRIC_PAIRS) {
    if (avgBs[l] != null && avgBs[r] != null) {
      result[`${l.replace('Left', '')}`] = +Math.abs(avgBs[l] - avgBs[r]).toFixed(4);
    }
  }
  return result;
}

function computeMovementVariability(bsSamples: Record<string, number[]>): number {
  let totalVar = 0; let count = 0;
  for (const key of FACS_KEYS) {
    const vals = bsSamples[key];
    if (!vals || vals.length < 5) continue;
    const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
    totalVar += vals.reduce((s, v) => s + (v - avg) ** 2, 0) / vals.length;
    count++;
  }
  return count > 0 ? +(totalVar / count).toFixed(6) : 0;
}

function mean(arr: number[]): number { return arr.reduce((s, v) => s + v, 0) / arr.length; }
function stddev(arr: number[], avg: number): number {
  return arr.length < 2 ? 0 : Math.sqrt(arr.reduce((s, v) => s + (v - avg) ** 2, 0) / (arr.length - 1));
}

// ── Calibrator ─────────────────────────────────────────────────────────

const MAX_DURATION = 30000;
const MIN_DURATION = 10000;
const MIN_SAMPLES = 60;

export class BaselineCalibrator implements IBaselineCalibrator {
  private baseline: any | undefined;
  private active = false;
  private startTime = 0;
  private samples: { valence: number; arousal: number }[] = [];
  private bsSamples: Record<string, number[]> = {};

  constructor(_config: any, _emitter: EventEmitter, private getters: Getters, private setters: Setters) {}

  /** Called by EIESession to begin calibration. Does NOT start its own timer. */
  start(): void {
    if (this.baseline || this.active) return;
    this.active = true;
    this.startTime = Date.now();
    this.samples = [];
    this.bsSamples = {};
    for (const key of FACS_KEYS) this.bsSamples[key] = [];
  }

  /**
   * Called externally by UltraDetectionLoop on every tick (~200ms).
   * Collects a sample if blendshapes are available, finalizes when ready.
   */
  feedSample(): void {
    if (!this.active || this.baseline) return;

    const elapsed = Date.now() - this.startTime;
    const shouldFinalize = (elapsed >= MIN_DURATION && this.samples.length >= MIN_SAMPLES) || elapsed >= MAX_DURATION;

    if (shouldFinalize) {
      this.finalize(elapsed);
      return;
    }

    const bs = this.getters.getLatestBlendshapes();
    if (!bs) return;

    for (const key of Object.keys(this.bsSamples)) {
      this.bsSamples[key].push(bs[key] ?? 0);
    }
    this.samples.push({ valence: computeValence(bs), arousal: computeArousal(bs) });

    const progress = Math.min(100, Math.round((this.samples.length / MIN_SAMPLES) * 100));
    this.setters.emit('calibrationProgress', { progress, samples: this.samples.length, required: MIN_SAMPLES });
  }

  private finalize(elapsed: number): void {
    this.active = false;

    if (this.samples.length < 5) {
      console.warn(`⚠️ Baseline calibration: only ${this.samples.length} samples after ${(elapsed / 1000).toFixed(1)}s, skipping`);
      return;
    }

    const n = this.samples.length;
    const avgV = mean(this.samples.map(s => s.valence));
    const avgA = mean(this.samples.map(s => s.arousal));
    const stdV = stddev(this.samples.map(s => s.valence), avgV);
    const stdA = stddev(this.samples.map(s => s.arousal), avgA);

    const avgBlendshapes: Record<string, number> = {};
    const stdBlendshapes: Record<string, number> = {};
    for (const key of Object.keys(this.bsSamples)) {
      const vals = this.bsSamples[key];
      if (vals.length < 5) continue;
      const avg = mean(vals);
      avgBlendshapes[key] = +avg.toFixed(4);
      stdBlendshapes[key] = +stddev(vals, avg).toFixed(4);
    }

    const personalizedThresholds: Record<string, number> = {};
    for (const key of Object.keys(avgBlendshapes)) {
      const a = avgBlendshapes[key];
      const s = stdBlendshapes[key];
      if (a == null || s == null) continue;
      personalizedThresholds[key] = Math.max(+(a + 1.5 * s).toFixed(3), DEFAULT_THRESHOLDS[key] ?? 0.25);
    }

    const facialTension = computeFacialTension(avgBlendshapes);
    const asymmetry = computeAsymmetry(avgBlendshapes);
    const movementVariability = computeMovementVariability(this.bsSamples);

    this.baseline = {
      avgValence: avgV, avgArousal: avgA,
      stdValence: stdV, stdArousal: stdA,
      avgBlendshapes, stdBlendshapes, personalizedThresholds,
      facialTension, asymmetry, movementVariability,
      sampleCount: n, timestamp: Date.now(),
    };

    this.setters.setBaseline(this.baseline);
    console.log(`✅ Baseline from ${n} samples: V=${avgV.toFixed(3)}±${stdV.toFixed(3)}, A=${avgA.toFixed(3)}±${stdA.toFixed(3)}, tension=${facialTension}, variability=${movementVariability}`);
    this.setters.emit('baselineCalibrated', this.baseline);

    const baselineState = { emotion: 'neutral', valence: avgV, arousal: avgA, confidence: 0.9, timestamp: Date.now() };
    this.setters.pushTimelineState(baselineState);
    this.setters.emit('emotionUpdate', baselineState);
  }

  stop(): void { this.active = false; }

  getBaseline(): any | undefined { return this.baseline; }
}
